import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Lock, MessageSquare, ArrowRight, Zap, Eye, TrendingUp } from "lucide-react";
import api from "@/lib/api";

/**
 * DiscordFomoLock — decorative overlay for paid members who haven't
 * linked Discord yet. Uses a static blurred "phantom setup" card so we
 * don't have to feed it real trade data — the FOMO is the point, not
 * the specifics.
 *
 * Props:
 *   variant: "setups" | "coach"  — swaps headline copy for context
 *   user: current user (checked for tier + discord_linked)
 */
export default function DiscordFomoLock({ variant = "setups", user }) {
  const { t } = useTranslation();
  const [linking, setLinking] = useState(false);

  const shouldShow = user
    && user.tier !== "free"
    && !user.discord_linked
    && user.role !== "admin";

  if (!shouldShow) return null;

  const link = async () => {
    setLinking(true);
    try {
      const { data } = await api.get("/discord/oauth/url");
      if (data?.url) window.location.href = data.url;
    } catch {
      setLinking(false);
    }
  };

  const copy = variant === "coach"
    ? {
        eyebrow:  t("discordFomoLockUi.coachEyebrow"),
        headline: t("discordFomoLockUi.coachHeadline"),
        body:     t("discordFomoLockUi.coachBody"),
      }
    : {
        eyebrow:  t("discordFomoLockUi.setupsEyebrow"),
        headline: t("discordFomoLockUi.setupsHeadline"),
        body:     t("discordFomoLockUi.setupsBody"),
      };

  return (
    <section
      data-testid={`discord-fomo-lock-${variant}`}
      className="relative rounded-3xl overflow-hidden bg-slate-950 text-white shadow-[0_30px_60px_-30px_rgba(15,23,42,0.6)] border border-slate-800/70"
    >
      {/* Phantom stack — static, always-on, no live data required. */}
      <div aria-hidden="true" className="relative h-[320px] sm:h-[380px] overflow-hidden">
        <div className="absolute inset-0 opacity-70 blur-[6px]">
          {/* Fake setup cards — pure CSS so this NEVER breaks with no data. */}
          <div className="p-6 space-y-4">
            {[
              { pair: "XAUUSD", dirKey: "discordFomoLockUi.short",  entry: "5,153", target: "5,015", r: "3.4R", tone: "rose" },
              { pair: "NAS100", dirKey: "discordFomoLockUi.long",   entry: "20,148", target: "20,540", r: "2.1R", tone: "emerald" },
              { pair: "EURUSD", dirKey: "discordFomoLockUi.long",   entry: "1.0862", target: "1.0918", r: "1.8R", tone: "emerald" },
              { pair: "US30",   dirKey: "discordFomoLockUi.short",  entry: "39,880", target: "39,540", r: "2.6R", tone: "rose" },
            ].map((c, i) => (
              <div key={i} className="rounded-xl bg-white/5 border border-white/10 backdrop-blur-sm p-4 flex items-center gap-4">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${c.tone === "emerald" ? "bg-emerald-500/20 text-emerald-300" : "bg-rose-500/20 text-rose-300"}`}>
                  <TrendingUp size={16} className={c.tone === "emerald" ? "" : "rotate-180"} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2 font-display">
                    <span className="font-bold text-lg tracking-tight">{c.pair}</span>
                    <span className={`text-[10px] uppercase tracking-widest font-bold ${c.tone === "emerald" ? "text-emerald-300" : "text-rose-300"}`}>{t(c.dirKey)}</span>
                  </div>
                  <div className="mt-0.5 text-xs text-white/60 font-mono tabular-nums">
                    {t("discordFomoLockUi.entry")} {c.entry} · {t("discordFomoLockUi.tp")} {c.target}
                  </div>
                </div>
                <div className="font-mono font-bold text-amber-300 text-sm">{c.r}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Radial darkening for readability, plus a soft indigo wash. */}
        <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-b from-slate-950/40 via-slate-950/70 to-slate-950" />
        <div aria-hidden="true" className="absolute inset-0 bg-[#5865F2]/10" />

        {/* Foreground content */}
        <div className="absolute inset-0 flex flex-col items-center justify-center px-6 sm:px-10 text-center">
          <div className="inline-flex items-center gap-1.5 rounded-full bg-white/10 border border-white/20 px-3 py-1 text-[10px] uppercase tracking-[0.28em] font-bold text-amber-300 mb-4">
            <Lock size={11} /> {copy.eyebrow}
          </div>
          <h3 className="font-display font-black text-2xl sm:text-3xl lg:text-4xl tracking-tighter max-w-2xl leading-[1.1]">
            {copy.headline}
          </h3>
          <p className="mt-3 text-sm sm:text-base text-white/75 max-w-xl leading-relaxed">
            {copy.body}
          </p>

          <div className="mt-6 flex flex-wrap items-center justify-center gap-2 text-[11px] text-white/60">
            <span className="inline-flex items-center gap-1"><Zap size={10} className="text-amber-300" /> {t("discordFomoLockUi.realTimeDrops")}</span>
            <span aria-hidden="true">·</span>
            <span className="inline-flex items-center gap-1"><Eye size={10} className="text-amber-300" /> {t("discordFomoLockUi.liveChat")}</span>
            <span aria-hidden="true">·</span>
            <span className="inline-flex items-center gap-1"><MessageSquare size={10} className="text-amber-300" /> {t("discordFomoLockUi.weeklyRecap")}</span>
          </div>

          <button
            type="button"
            onClick={link}
            disabled={linking}
            data-testid={`discord-fomo-lock-${variant}-cta`}
            className="mt-6 inline-flex items-center gap-2 rounded-md bg-[#5865F2] hover:bg-[#4752c4] disabled:opacity-60 text-white font-semibold px-5 py-3 text-sm shadow-lg transition-colors"
          >
            {linking
              ? t("discordFomoLockUi.linking")
              : t("discordFomoLockUi.cta")}
            <ArrowRight size={14} />
          </button>
          <p className="mt-2 text-[11px] text-white/50">
            {t("discordFomoLockUi.trust")}
          </p>
        </div>
      </div>
    </section>
  );
}
